import { clearInvitationAcceptanceSession } from '@common/modules/invitation/utils/clearInvitationAcceptanceSession'
import { hasRecentInvitationAcceptanceInSession } from '@common/modules/invitation/utils/hasRecentInvitationAcceptanceInSession'
import { addProject, getCurrentProject } from '@common/modules/projects/stores/projects.actions'
import { useProjectsStore } from '@common/modules/projects/stores/projects.store'
import { updateWorkspaceContext } from '@common/modules/user'
import { createWorkspace } from '@common/modules/workspaces/stores/workspaces.actions'
import { useWorkspacesStore } from '@common/modules/workspaces/stores/workspaces.store'

import { DEFAULT_PROJECT_NAME, DEFAULT_WORKSPACE_NAME } from '../const/default-names.const'

import { loadWorkspaceFromStore } from './loadWorkspaceFromStore'
import { syncProjectAfterCreate } from './syncProjectAfterCreate'
import { waitForWorkspaceInStore } from './waitForWorkspaceInStore'

async function syncExistingWorkspace(workspaceId: string): Promise<void> {
  const workspace = loadWorkspaceFromStore()

  if (!workspace) {
    return
  }

  useWorkspacesStore.getState().setCurrentWorkspace(workspace)
  await updateWorkspaceContext(workspaceId)

  if (!useProjectsStore.getState().currentProject) {
    await getCurrentProject(workspaceId)
  }

  if (!useProjectsStore.getState().currentProject) {
    const createdProjectId = await addProject({
      name: DEFAULT_PROJECT_NAME,
      workspaceId,
      description: 'Our first project',
    })
    await syncProjectAfterCreate(workspaceId, createdProjectId)
  }
}

export async function runOnboardingWorkspaceSetup(): Promise<void> {
  const fromInvitation = hasRecentInvitationAcceptanceInSession()
  const workspace = fromInvitation
    ? await waitForWorkspaceInStore(5, 1000)
    : ((await waitForWorkspaceInStore(1, 0)) ?? loadWorkspaceFromStore())

  if (fromInvitation) {
    clearInvitationAcceptanceSession()
  }

  if (workspace) {
    await syncExistingWorkspace(workspace.id)
    return
  }

  if (fromInvitation) {
    throw new Error('Workspace not available after accepting invitation')
  }

  console.warn('[Onboarding] creating default workspace', {
    defaultName: DEFAULT_WORKSPACE_NAME,
  })

  const createdWorkspaceKey = await createWorkspace(DEFAULT_WORKSPACE_NAME)

  if (!createdWorkspaceKey) {
    useProjectsStore.getState().setCurrentProject(null)
    return
  }

  const createdWorkspace = await waitForWorkspaceInStore(3, 500)
  if (!createdWorkspace) {
    useProjectsStore.getState().setCurrentProject(null)
    return
  }

  useWorkspacesStore.getState().setCurrentWorkspace(createdWorkspace)
  await updateWorkspaceContext(createdWorkspace.id)

  const createdProjectId = await addProject({
    name: DEFAULT_PROJECT_NAME,
    workspaceId: createdWorkspace.id,
    description: 'Our first project',
  })
  await syncProjectAfterCreate(createdWorkspace.id, createdProjectId)
}
